import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SettingsDrawer.css';
import PlatformIntegrations from '../Settings/PlatformIntegrations';
import DatabaseManagement from '../Settings/DatabaseManagement';

interface SettingsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    apiBaseUrl: string;
    aiConfig?: any;
    aiMode?: string;
}

interface Settings {
    ai: {
        provider: string;
        model: string;
        temperature: number;
        max_tokens: number;
    };
    resources: {
        max_file_size_mb: number;
        execution_timeout_seconds: number;
        cpu_cores: number;
        memory_gb: number;
        gpu_enabled: boolean;
        gpu_type: string;
    };
    application: {
        default_type: string;
        supported_types: string[];
    };
    mocking: {
        default_delay_ms: number;
        intelligent_mode: boolean;
    };
    integrations: {
        configured: string[];
    };
}

const SettingsDrawer: React.FC<SettingsDrawerProps> = ({ isOpen, onClose, apiBaseUrl, aiConfig, aiMode }) => {
    const [settings, setSettings] = useState<Settings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState('ai');
    const [aiProviders, setAiProviders] = useState<any>({});
    const [modelRefreshing, setModelRefreshing] = useState(false);
    const [modelRefreshError, setModelRefreshError] = useState<string | null>(null);

    const [detectedResources, setDetectedResources] = useState<any>(null);
    const [resourcesLoading, setResourcesLoading] = useState(false);

    // Fetch detected resources when drawer opens
    useEffect(() => {
        if (isOpen) {
            fetchDetectedResources();
        }
    }, [isOpen]);

    const fetchDetectedResources = async () => {
        setResourcesLoading(true);
        try {
            const response = await fetch(`${apiBaseUrl}/system/resources`);
            const data = await response.json();
            setDetectedResources(data);
        } catch (error) {
            console.error('Failed to fetch system resources:', error);
        }
        setResourcesLoading(false);
    };

    useEffect(() => {
        if (isOpen) {
            loadSettings();
        }
    }, [isOpen]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiBaseUrl}/settings`);
            //console.log('Raw settings from backend:', response.data);

            const aiConfigResponse = await axios.get(`${apiBaseUrl}/ai-config`);
            //console.log('AI Providers:', aiConfigResponse.data.providers);
            setAiProviders(aiConfigResponse.data.providers || {});

            // Use the aiMode and aiConfig passed from props to determine current provider
            const currentModel = aiConfig?.default_model || 'gpt-4';
            let currentProvider = 'openai'; // default

            // Find which provider has the current model
            if (aiMode === 'local') {
                currentProvider = 'mistral-local';
            } else {
                // Find the provider that contains the current model
                for (const [providerId, providerData] of Object.entries(aiConfigResponse.data.providers || {})) {
                    const provider = providerData as any;
                    if (provider.models?.some((m: any) => m.id === currentModel)) {
                        currentProvider = providerId;
                        break;
                    }
                }
            }

            // Parse JSON strings in the response
            const parsedSettings = {
                ai: {
                    provider: currentProvider,  // Use detected provider instead of parsing from settings
                    model: currentModel,         // Use current model from aiConfig
                    temperature: parseFloat(response.data.ai.temperature),
                    max_tokens: parseInt(response.data.ai.max_tokens)
                },
                resources: {
                    max_file_size_mb: parseInt(response.data.resources.max_file_size_mb),
                    execution_timeout_seconds: parseInt(response.data.resources.execution_timeout_seconds),
                    cpu_cores: parseInt(response.data.resources.cpu_cores),
                    memory_gb: parseInt(response.data.resources.memory_gb),
                    gpu_enabled: response.data.resources.gpu_enabled === 'true',
                    gpu_type: JSON.parse(response.data.resources.gpu_type)
                },
                application: {
                    default_type: JSON.parse(response.data.application.default_type),
                    supported_types: JSON.parse(response.data.application.supported_types)
                },
                mocking: {
                    default_delay_ms: parseInt(response.data.mocking.default_delay_ms),
                    intelligent_mode: response.data.mocking.intelligent_mode === 'true'
                },
                integrations: {
                    configured: JSON.parse(response.data.integrations.configured)
                }
            };
            setSettings(parsedSettings);
        } catch (error) {
            console.error('Failed to load settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async (category: string, values: any) => {
        try {
            setSaving(true);
            //console.log('Saving category:', category, 'with values:', values); // ADD THIS LINE
            await axios.post(`${apiBaseUrl}/settings/${category}`, values);
            alert('Settings saved successfully');
        } catch (error) {
            console.error('Failed to save settings:', error);
            alert('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    // Fetch the live model list for a provider from the backend and merge it in.
    // selectFirst=true picks the first returned model (used when the provider changes).
    const refreshModels = async (providerId: string, selectFirst: boolean = false) => {
        if (!providerId) return;
        try {
            setModelRefreshing(true);
            setModelRefreshError(null);
            const resp = await axios.get(`${apiBaseUrl}/ai-providers/${providerId}/models/refresh`);
            const models = resp.data?.models || [];
            if (models.length > 0) {
                setAiProviders((prev: any) => ({
                    ...prev,
                    [providerId]: { ...(prev[providerId] || {}), models }
                }));
                if (selectFirst) {
                    setSettings(prev => prev ? { ...prev, ai: { ...prev.ai, model: models[0].id } } : prev);
                }
            }
            if (resp.data?.source === 'static') {
                setModelRefreshError('Showing built-in list — live fetch unavailable (check API key / Ollama running)');
            }
        } catch (e) {
            console.error('Model refresh failed:', e);
            setModelRefreshError('Could not refresh models from provider');
        } finally {
            setModelRefreshing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="settings-overlay" onClick={onClose} />
            <div className="settings-drawer">
                <div className="settings-header">
                    <h2>Platform Settings</h2>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="settings-nav">
                    <button
                        className={activeSection === 'ai' ? 'active' : ''}
                        onClick={() => setActiveSection('ai')}
                    >
                        AI
                    </button>

                    {/*
                    <button
                        className={activeSection === 'resources' ? 'active' : ''}
                        onClick={() => setActiveSection('resources')}
                    >
                        Resources
                    </button>
                    */}
                    <button
                        className={activeSection === 'system' ? 'active' : ''}
                        onClick={() => setActiveSection('system')}
                    >
                        System
                    </button>
                    <button
                        className={activeSection === 'application' ? 'active' : ''}
                        onClick={() => setActiveSection('application')}
                    >
                        Application
                    </button>
                    <button
                        className={activeSection === 'database' ? 'active' : ''}
                        onClick={() => setActiveSection('database')}
                    >
                        Database
                    </button>

                    <button
                        className={activeSection === 'integrations' ? 'active' : ''}
                        onClick={() => setActiveSection('integrations')}
                    >
                        Integrations
                    </button>
                </div>

                <div className="settings-content">
                    {loading ? (
                        <div className="loading">Loading settings...</div>
                    ) : settings && (
                        <>
                            {activeSection === 'ai' && (
                                <div className="settings-section">
                                    <h3>AI Configuration</h3>
                                    <div className="setting-group">
                                        <label>Provider</label>
                                        <select
                                            value={settings.ai.provider}
                                            onChange={(e) => {
                                                const newProvider = e.target.value;
                                                const provider = aiProviders[newProvider];
                                                const firstModel = provider?.models?.[0]?.id || '';

                                                setSettings({
                                                    ...settings,
                                                    ai: {
                                                        ...settings.ai,
                                                        provider: newProvider,
                                                        model: firstModel
                                                    }
                                                });

                                                // Auto-fetch the live model list for the newly selected provider
                                                refreshModels(newProvider, true);
                                            }}
                                        >
                                            {Object.entries(aiProviders).map(([key, provider]: [string, any]) => (
                                                <option key={key} value={key}>
                                                    {provider.display_name || provider.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="setting-group">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>Model</span>
                                            <button
                                                type="button"
                                                onClick={() => refreshModels(settings.ai.provider)}
                                                disabled={modelRefreshing}
                                                title="Fetch the latest models from this provider"
                                                style={{
                                                    cursor: modelRefreshing ? 'wait' : 'pointer',
                                                    border: '1px solid #3b82f6',
                                                    background: 'transparent',
                                                    color: '#3b82f6',
                                                    borderRadius: '6px',
                                                    padding: '2px 8px',
                                                    fontSize: '0.8em',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {modelRefreshing ? '↻ Refreshing…' : '↻ Refresh'}
                                            </button>
                                        </label>
                                        <select
                                            value={settings.ai.model}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                ai: { ...settings.ai, model: e.target.value }
                                            })}
                                        >
                                            {aiProviders[settings.ai.provider]?.models?.map((model: any) => (
                                                <option key={model.id} value={model.id}>
                                                    {model.name}
                                                </option>
                                            ))}
                                        </select>
                                        {modelRefreshError && (
                                            <small style={{ color: '#f59e0b', display: 'block', marginTop: '4px' }}>
                                                {modelRefreshError}
                                            </small>
                                        )}
                                    </div>
                                    <div className="setting-group">
                                        <label>Temperature: {settings.ai.temperature}</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={settings.ai.temperature}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                ai: { ...settings.ai, temperature: parseFloat(e.target.value) }
                                            })}
                                        />
                                    </div>
                                    <button
                                        className="save-btn"
                                        onClick={() => saveSettings('ai', settings.ai)}
                                        disabled={saving}
                                    >
                                        Save AI Settings
                                    </button>
                                </div>
                            )}

                            {activeSection === 'resources' && (
                                <div className="settings-section">
                                    <h3>Resource Configuration</h3>

                                    {detectedResources && (
                                        <div className="resource-info-banner">
                                            <h4>System Information</h4>
                                            <div className="info-grid">
                                                <div className="info-item">
                                                    <label>Environment:</label>
                                                    <span>{detectedResources.host?.environment || detectedResources.cloud?.provider || 'Unknown'}</span>
                                                </div>
                                                <div className="info-item">
                                                    <label>Available CPU:</label>
                                                    <span>{detectedResources.cpu?.physical_cores || 'Unknown'} cores</span>
                                                </div>
                                                <div className="info-item">
                                                    <label>Available Memory:</label>
                                                    <span>{detectedResources.memory?.total_gb || 'Unknown'} GB</span>
                                                </div>
                                                {detectedResources.host?.memory_allocation_status && (
                                                    <div className="info-item warning">
                                                        <label>Docker Memory:</label>
                                                        <span>{detectedResources.host.docker_allocated_memory} - {detectedResources.host.memory_allocation_status}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {detectedResources.host?.recommended_action && (
                                                <div className="recommendation-banner">
                                                    💡 {detectedResources.host.recommended_action}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {detectedResources?.recommendations && (
                                        <div className="resource-profiles">
                                            <h4>Quick Profiles</h4>
                                            <div className="profile-buttons">
                                                <button
                                                    className="profile-btn"
                                                    onClick={() => {
                                                        setSettings({
                                                            ...settings,
                                                            resources: {
                                                                ...settings.resources,
                                                                cpu_cores: detectedResources.recommendations.cpu.development,
                                                                memory_gb: detectedResources.recommendations.memory.development
                                                            }
                                                        });
                                                    }}
                                                >
                                                    Development
                                                    <small>{detectedResources.recommendations.cpu.development} CPU, {detectedResources.recommendations.memory.development}GB RAM</small>
                                                </button>
                                                <button
                                                    className="profile-btn"
                                                    onClick={() => {
                                                        setSettings({
                                                            ...settings,
                                                            resources: {
                                                                ...settings.resources,
                                                                cpu_cores: detectedResources.recommendations.cpu.testing,
                                                                memory_gb: detectedResources.recommendations.memory.testing
                                                            }
                                                        });
                                                    }}
                                                >
                                                    Testing
                                                    <small>{detectedResources.recommendations.cpu.testing} CPU, {detectedResources.recommendations.memory.testing}GB RAM</small>
                                                </button>
                                                <button
                                                    className="profile-btn"
                                                    onClick={() => {
                                                        setSettings({
                                                            ...settings,
                                                            resources: {
                                                                ...settings.resources,
                                                                cpu_cores: detectedResources.recommendations.cpu.performance,
                                                                memory_gb: detectedResources.recommendations.memory.performance
                                                            }
                                                        });
                                                    }}
                                                >
                                                    Performance
                                                    <small>{detectedResources.recommendations.cpu.performance} CPU, {detectedResources.recommendations.memory.performance}GB RAM</small>
                                                </button>
                                            </div>
                                        </div>
                                    )}



                                    <div className="setting-group">
                                        <label>Max File Size (MB)</label>
                                        <input
                                            type="number"
                                            value={settings.resources.max_file_size_mb}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                resources: { ...settings.resources, max_file_size_mb: parseInt(e.target.value) }
                                            })}
                                        />
                                    </div>
                                    <div className="setting-group">
                                        <label>Execution Timeout (seconds)</label>
                                        <input
                                            type="number"
                                            value={settings.resources.execution_timeout_seconds}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                resources: { ...settings.resources, execution_timeout_seconds: parseInt(e.target.value) }
                                            })}
                                        />
                                    </div>
                                    <div className="setting-group">
                                        <label>CPU Cores</label>
                                        <select
                                            value={settings.resources.cpu_cores}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                const maxCpu = detectedResources?.recommendations?.cpu?.maximum || 32;
                                                if (value > maxCpu) {
                                                    alert(`CPU cores cannot exceed ${maxCpu} (available on system)`);
                                                    return;
                                                }
                                                setSettings({
                                                    ...settings,
                                                    resources: { ...settings.resources, cpu_cores: value }
                                                });
                                            }}
                                        >
                                            {[1, 2, 4, 8, 16, 32].filter(cores =>
                                                cores <= (detectedResources?.recommendations?.cpu?.maximum || 32)
                                            ).map(cores => (
                                                <option key={cores} value={cores}>{cores} cores</option>
                                            ))}
                                        </select>
                                        {detectedResources && settings.resources.cpu_cores > detectedResources.recommendations?.cpu?.maximum && (
                                            <span className="validation-warning">⚠️ Exceeds available resources</span>
                                        )}
                                    </div>
                                    <div className="setting-group">
                                        <label>Memory (GB)</label>
                                        <select
                                            value={settings.resources.memory_gb}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                const maxMem = detectedResources?.recommendations?.memory?.maximum || 64;
                                                if (value > maxMem) {
                                                    alert(`Memory cannot exceed ${maxMem} GB (available on system)`);
                                                    return;
                                                }
                                                setSettings({
                                                    ...settings,
                                                    resources: { ...settings.resources, memory_gb: value }
                                                });
                                            }}
                                        >
                                            {[1, 2, 4, 8, 16, 32, 64].filter(gb =>
                                                gb <= (detectedResources?.recommendations?.memory?.maximum || 64)
                                            ).map(gb => (
                                                <option key={gb} value={gb}>{gb} GB</option>
                                            ))}
                                        </select>
                                        {detectedResources && settings.resources.memory_gb > detectedResources.recommendations?.memory?.maximum && (
                                            <span className="validation-warning">⚠️ Exceeds available memory</span>
                                        )}
                                    </div>
                                    <div className="setting-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={settings.resources.gpu_enabled}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    resources: { ...settings.resources, gpu_enabled: e.target.checked }
                                                })}
                                            />
                                            Enable GPU
                                        </label>
                                    </div>
                                    {settings.resources.gpu_enabled && (
                                        <div className="setting-group">
                                            <label>GPU Type</label>
                                            <select
                                                value={settings.resources.gpu_type}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    resources: { ...settings.resources, gpu_type: e.target.value }
                                                })}
                                            >
                                                <option value="none">None</option>
                                                <option value="T4">T4</option>
                                                <option value="V100">V100</option>
                                                <option value="A100">A100</option>
                                            </select>
                                            {detectedResources?.gpu?.available && (
                                                <div className="gpu-info">
                                                    <small>Detected: {detectedResources.gpu.devices[0]?.name || 'GPU Available'}</small>
                                                    {detectedResources.gpu.devices[0]?.note && (
                                                        <small className="gpu-note" style={{ display: 'block', color: '#999' }}>
                                                            {detectedResources.gpu.devices[0].note}
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        className="save-btn"
                                        onClick={() => saveSettings('resources', settings.resources)}
                                        disabled={saving}
                                    >
                                        Save Resource Settings
                                    </button>
                                </div>
                            )}

                            {activeSection === 'database' && (
                                <div className="settings-section">
                                    <DatabaseManagement apiBaseUrl={apiBaseUrl} />
                                </div>
                            )}

                            {/* Add more sections as needed */}
                            {activeSection === 'integrations' && (
                                <div className="settings-section">
                                    <PlatformIntegrations />
                                </div>
                            )}


                            {activeSection === 'system' && (
                                <div className="settings-section">
                                    <h3>System Controls</h3>
                                    <div className="system-buttons-grid" style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '1rem',
                                        marginTop: '1rem'
                                    }}>
                                        <button
                                            className="system-control-btn"
                                            onClick={() => window.dispatchEvent(new CustomEvent('system-action', { detail: 'environment' }))}
                                            style={{
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <span style={{ fontSize: '2rem' }}>🌍</span>
                                            <span>Environment Status</span>
                                        </button>



                                        <button
                                            className="system-control-btn"
                                            onClick={() => window.dispatchEvent(new CustomEvent('system-action', { detail: 'logs' }))}
                                            style={{
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <span style={{ fontSize: '2rem' }}>📋</span>
                                            <span>System Logs</span>
                                        </button>

                                        <button
                                            className="system-control-btn"
                                            onClick={() => window.dispatchEvent(new CustomEvent('system-action', { detail: 'admin' }))}
                                            style={{
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <span style={{ fontSize: '2rem' }}>👤</span>
                                            <span>Admin Console</span>
                                        </button>


                                        <button
                                            className="system-control-btn"
                                            onClick={() => window.dispatchEvent(new CustomEvent('system-action', { detail: 'resources' }))}
                                            style={{
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <span style={{ fontSize: '2rem' }}>💻</span>
                                            <span>SW/HW Resources</span>
                                        </button>



                                    </div>
                                </div>
                            )}



                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SettingsDrawer;